'use client';

import { useState, useMemo, useEffect } from 'react';
import {
  BookOpen,
  Clock,
  PlayCircle,
  ShoppingCart,
  Check,
  ArrowRight,
  SlidersHorizontal,
  Gift,
  Tag,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { TestModule, Course } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { usePreferences } from '@/context/PreferencesContext';
import { PersonalizationLauncher } from '@/components/personalization/PersonalizationLauncher';
import { useTranslations } from 'next-intl';
import { filterBySegment, sortByInterest } from '@/lib/catalogPersonalization';
import { useSegmentPolicy } from '@/hooks/useSegmentPolicy';
import { CatalogEmptyState } from '@/components/catalog/CatalogEmptyState';
import { CoverPlaceholder } from '@/components/ui/CoverPlaceholder';
import { PAGE_CONTAINER, Ribbon } from '@/components/ui';

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';

/* ── Helpers ────────────────────────────────────────── */

function formatModuleDuration(tests: { durationMins: number }[], waktuFleksibel: string): string {
  const total = tests.reduce((sum, t) => sum + (t.durationMins ?? 0), 0);
  if (!total) return waktuFleksibel;
  if (total < 60) return `${total} menit`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m > 0 ? `${h} jam ${m} menit` : `${h} jam`;
}

function formatDuration(mins: number): string {
  if (!mins) return '';
  if (mins < 60) return `${mins} menit`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} jam ${m} menit` : `${h} jam`;
}

/* ── Price display ──────────────────────────────────── */

function PriceTag({ isFree, price, basePrice, promoPrice, size = 'sm', gratisLabel }: {
  isFree: boolean;
  price: number;
  basePrice: number;
  promoPrice: number | null;
  size?: 'sm' | 'lg';
  gratisLabel: string;
}) {
  const hasDiscount = promoPrice != null && promoPrice < basePrice;
  const textSize = size === 'lg' ? 'text-xl sm:text-2xl' : 'text-sm sm:text-base';
  const strikeSize = size === 'lg' ? 'text-sm' : 'text-xs';

  if (isFree) return <span className={`font-extrabold ${textSize} text-emerald-600`}>{gratisLabel}</span>;
  if (hasDiscount) {
    return (
      <span className="flex items-baseline gap-1 flex-wrap">
        <span className={`font-extrabold ${textSize} text-orange-600`}>{`Rp ${promoPrice!.toLocaleString('id-ID')}`}</span>
        <span className={`${strikeSize} text-slate-400 line-through`}>{`Rp ${basePrice.toLocaleString('id-ID')}`}</span>
      </span>
    );
  }
  return <span className={`font-extrabold ${textSize} text-slate-900`}>{`Rp ${price.toLocaleString('id-ID')}`}</span>;
}

/* ── CTA Button ─────────────────────────────────────── */

function ModuleCTA({ module, size = 'sm', labels }: {
  module: TestModule;
  size?: 'sm' | 'lg';
  labels: { mulai: string; diKeranjang: string; cobaGratis: string; ikuti: string };
}) {
  const { addItem, openDrawer, isInCart } = useCart();
  const { ownedModuleIds } = useOwnedItems();

  const isOwned = ownedModuleIds.has(module.id);
  const inCart = isInCart(module.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOwned || inCart) return;
    addItem({ type: 'module', moduleId: module.id, title: module.title, price: module.price, basePrice: module.basePrice, promoPrice: module.promoPrice, isFree: module.isFree, coverImageUrl: module.coverImageUrl });
    openDrawer();
  };

  const cls = size === 'lg'
    ? 'px-5 py-2.5 min-h-[36px] text-sm rounded-xl'
    : 'px-3 py-2 min-h-[36px] text-xs rounded-lg';
  const iconCls = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  if (isOwned) {
    return (
      <a href={`${DASHBOARD_URL}/tests`} onClick={(e) => e.stopPropagation()} className={`${cls} inline-flex items-center gap-1.5 font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors`}>
        {labels.mulai} <ArrowRight className={iconCls} />
      </a>
    );
  }
  if (inCart) {
    return (
      <span className={`${cls} inline-flex items-center gap-1 font-semibold text-teal-600 bg-teal-50`}>
        <Check className={iconCls} /> {labels.diKeranjang}
      </span>
    );
  }
  if (module.isFree) {
    return (
      <button onClick={handleAddToCart} className={`${cls} inline-flex items-center gap-1.5 font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors`}>
        {labels.cobaGratis} <ArrowRight className={iconCls} />
      </button>
    );
  }
  return (
    <button onClick={handleAddToCart} className={`${cls} inline-flex items-center gap-1.5 font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors`}>
      <ShoppingCart className={iconCls} /> {labels.ikuti}
    </button>
  );
}

function CourseCTA({ course, size = 'sm', labels }: {
  course: Course;
  size?: 'sm' | 'lg';
  labels: { mulai: string; diKeranjang: string; cobaGratis: string; daftar: string };
}) {
  const { addItem, openDrawer, isInCart } = useCart();
  const { ownedCourseIds } = useOwnedItems();

  const isOwned = ownedCourseIds.has(course.id);
  const inCart = isInCart(course.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOwned || inCart) return;
    addItem({ type: 'course', moduleId: course.id, title: course.title, price: course.basePrice ?? 0, basePrice: course.basePrice ?? 0, promoPrice: course.promoPrice, isFree: course.isFree, coverImageUrl: course.coverImageUrl });
    openDrawer();
  };

  const cls = size === 'lg'
    ? 'px-5 py-2.5 min-h-[36px] text-sm rounded-xl'
    : 'px-3 py-2 min-h-[36px] text-xs rounded-lg';
  const iconCls = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  if (isOwned) {
    return (
      <a href={`${DASHBOARD_URL}/courses`} onClick={(e) => e.stopPropagation()} className={`${cls} inline-flex items-center gap-1.5 font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors`}>
        {labels.mulai} <ArrowRight className={iconCls} />
      </a>
    );
  }
  if (inCart) {
    return (
      <span className={`${cls} inline-flex items-center gap-1 font-semibold text-teal-600 bg-teal-50`}>
        <Check className={iconCls} /> {labels.diKeranjang}
      </span>
    );
  }
  if (course.isFree) {
    return (
      <button onClick={handleAddToCart} className={`${cls} inline-flex items-center gap-1.5 font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors`}>
        {labels.cobaGratis} <ArrowRight className={iconCls} />
      </button>
    );
  }
  return (
    <button onClick={handleAddToCart} className={`${cls} inline-flex items-center gap-1.5 font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors`}>
      <ShoppingCart className={iconCls} /> {labels.daftar}
    </button>
  );
}

/* ── Featured (big) cards ───────────────────────────── */
// The featured cover is the mobile LCP element, so it gets `priority` (preload +
// eager + fetchpriority=high). Only one featured card renders at a time; the compact
// grid covers below stay lazy.

// A card is a link, unless a prerequisite gate makes it inert. Kept as one shell
// so the two homepage cards below cannot drift apart, and so a locked module is
// non-interactive here exactly as it is on /tests. Rendering a dimmed <Link> would
// look disabled while still navigating, which is the worst of both.
function CardShell({
  isLocked,
  href,
  className,
  lockedClassName,
  ribbon,
  children,
}: {
  isLocked: boolean;
  href: string;
  className: string;
  lockedClassName: string;
  ribbon: React.ReactNode;
  children: React.ReactNode;
}) {
  if (isLocked) {
    return <div className={lockedClassName} aria-disabled="true">{children}</div>;
  }
  // The wrapper is not clipped, so the ribbon can overhang the card edge; the Link
  // keeps overflow-hidden for the rounded cover. A locked card gets no ribbon.
  return (
    <div className="relative h-full">
      <Link href={href} className={className}>{children}</Link>
      {ribbon}
    </div>
  );
}

// One ribbon at most: free wins over discount. discountPct is 0 without a discount.
function CardRibbon({ isFree, discountPct, gratisLabel }: {
  isFree: boolean;
  discountPct: number;
  gratisLabel: string;
}) {
  if (isFree) return <Ribbon tone="free" icon={Gift} label={gratisLabel} />;
  if (discountPct > 0) return <Ribbon tone="discount" icon={Tag} label={`-${discountPct}%`} />;
  return null;
}

function FeaturedModuleCard({ module, labels }: {
  module: TestModule;
  labels: { gratis: string; ujian: string; waktuFleksibel: string; mulai: string; diKeranjang: string; cobaGratis: string; ikuti: string };
}) {
  const accentColor = module.examTypes[0]?.color ?? '#0f766e';
  const { completedModuleIds } = useOwnedItems();
  // Gated behind an unfinished prerequisite. Without this the same module rendered
  // locked on /tests and clickable here.
  const isLocked =
    !!module.unlockAfterModuleId && !completedModuleIds.has(module.unlockAfterModuleId);
  const hasDiscount = module.promoPrice != null && module.promoPrice < module.basePrice;
  const discountPct = hasDiscount ? Math.round(((module.basePrice - module.promoPrice!) / module.basePrice) * 100) : 0;

  return (
    <CardShell
      isLocked={isLocked}
      ribbon={<CardRibbon isFree={module.isFree} discountPct={discountPct} gratisLabel={labels.gratis} />}
      href={`/tests/${module.slug}`}
      className="group flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-teal-200 hover:shadow-lg transition-all h-full"
      lockedClassName="flex flex-col bg-slate-50 rounded-2xl border border-dashed border-slate-200 overflow-hidden h-full opacity-60 cursor-not-allowed"
    >
      <div className="relative aspect-[16/9] lg:aspect-auto lg:flex-1 lg:min-h-[22rem] overflow-hidden bg-slate-100">
        {module.coverImageUrl ? (
          <Image src={module.coverImageUrl} alt={module.title} fill priority sizes="(max-width: 1024px) 100vw, 66vw" className="object-cover object-left group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <CoverPlaceholder label={module.examTypes[0]?.name ?? module.title} accentColor={module.examTypes[0]?.color ?? accentColor} />
        )}
      </div>
      <div className="p-4 sm:p-5 shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {module.examTypes.slice(0, 1).map(et => (
            <span key={et.slug} className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}>
              {et.name}
            </span>
          ))}
        </div>
        <h3 className="font-bold text-slate-900 text-xl sm:text-2xl leading-snug mb-2 line-clamp-2 group-hover:text-teal-700 transition-colors">{module.title}</h3>
        <p className="text-slate-500 text-sm mb-3 line-clamp-3">{module.description}</p>
        <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
          <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4" />{module.tests?.length || 0} {labels.ujian}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{formatModuleDuration(module.tests ?? [], labels.waktuFleksibel)}</span>
        </div>
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
          <PriceTag isFree={module.isFree} price={module.price} basePrice={module.basePrice} promoPrice={module.promoPrice} size="lg" gratisLabel={labels.gratis} />
          <ModuleCTA module={module} size="lg" labels={labels} />
        </div>
      </div>
    </CardShell>
  );
}

function FeaturedCourseCard({ course, labels }: {
  course: Course;
  labels: { gratis: string; materi: string; mulai: string; diKeranjang: string; cobaGratis: string; daftar: string };
}) {
  const accentColor = course.examTypes[0]?.color ?? '#0f766e';
  const hasDiscount = course.promoPrice != null && course.basePrice != null && course.promoPrice < course.basePrice;
  const discountPct = hasDiscount ? Math.round(((course.basePrice! - course.promoPrice!) / course.basePrice!) * 100) : 0;

  return (
    <div className="relative h-full">
    <Link href={`/courses/${course.slug}`} className="group flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-teal-200 hover:shadow-lg transition-all h-full">
      <div className="relative aspect-[16/9] lg:aspect-auto lg:flex-1 lg:min-h-[22rem] overflow-hidden bg-slate-100">
        {course.coverImageUrl ? (
          <Image src={course.coverImageUrl} alt={course.title} fill priority sizes="(max-width: 1024px) 100vw, 66vw" className="object-cover object-left group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <CoverPlaceholder label={course.examTypes[0]?.name ?? course.title} accentColor={course.examTypes[0]?.color ?? accentColor} />
        )}
      </div>
      <div className="p-4 sm:p-5 shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {course.examTypes.slice(0, 1).map(et => (
            <span key={et.slug} className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}>
              {et.name}
            </span>
          ))}
        </div>
        <h3 className="font-bold text-slate-900 text-xl sm:text-2xl leading-snug mb-2 line-clamp-2 group-hover:text-teal-700 transition-colors">{course.title}</h3>
        <p className="text-slate-500 text-sm mb-3 line-clamp-3">{course.shortDescription ?? course.description}</p>
        <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
          <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4" />{course.totalLessons} {labels.materi}</span>
          {course.totalDurationMins > 0 && <span className="flex items-center gap-1.5"><PlayCircle className="w-4 h-4" />{formatDuration(course.totalDurationMins)}</span>}
        </div>
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
          <PriceTag isFree={course.isFree} price={course.basePrice ?? 0} basePrice={course.basePrice ?? 0} promoPrice={course.promoPrice} size="lg" gratisLabel={labels.gratis} />
          <CourseCTA course={course} size="lg" labels={labels} />
        </div>
      </div>
    </Link>
    <CardRibbon isFree={course.isFree} discountPct={discountPct} gratisLabel={labels.gratis} />
    </div>
  );
}

/* ── Compact (small) cards ──────────────────────────── */

function CompactModuleCard({ module, labels }: {
  module: TestModule;
  labels: { gratis: string; ujian: string; waktuFleksibel: string; mulai: string; diKeranjang: string; cobaGratis: string; ikuti: string };
}) {
  const accentColor = module.examTypes[0]?.color ?? '#0f766e';
  const { completedModuleIds } = useOwnedItems();
  // Gated behind an unfinished prerequisite. Without this the same module rendered
  // locked on /tests and clickable here.
  const isLocked =
    !!module.unlockAfterModuleId && !completedModuleIds.has(module.unlockAfterModuleId);
  const hasDiscount = module.promoPrice != null && module.promoPrice < module.basePrice;
  const discountPct = hasDiscount ? Math.round(((module.basePrice - module.promoPrice!) / module.basePrice) * 100) : 0;
  const duration = formatModuleDuration(module.tests ?? [], labels.waktuFleksibel);

  return (
    <CardShell
      isLocked={isLocked}
      ribbon={<CardRibbon isFree={module.isFree} discountPct={discountPct} gratisLabel={labels.gratis} />}
      href={`/tests/${module.slug}`}
      className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-200 hover:shadow-md transition-all h-full"
      lockedClassName="flex flex-col bg-slate-50 rounded-xl border border-dashed border-slate-200 overflow-hidden h-full opacity-60 cursor-not-allowed"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
        {module.coverImageUrl ? (
          <Image src={module.coverImageUrl} alt={module.title} fill sizes="(max-width: 640px) 50vw, 33vw" className="object-cover object-left group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <CoverPlaceholder label={module.examTypes[0]?.name ?? module.title} accentColor={module.examTypes[0]?.color ?? accentColor} compact />
        )}
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 flex-wrap mb-1.5">
          {module.examTypes.slice(0, 1).map(et => (
            <span key={et.slug} className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}>
              {et.name}
            </span>
          ))}
        </div>
        <h4 className="font-bold text-slate-900 mb-1 line-clamp-2 text-sm sm:text-base group-hover:text-teal-700 transition-colors">{module.title}</h4>
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2 mt-auto">
          <span className="flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{module.tests?.length || 0} {labels.ujian}</span>
          {duration !== labels.waktuFleksibel && (
            <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{duration}</span>
          )}
        </div>
        <div className="pt-2 sm:pt-3 border-t border-slate-100 flex flex-col gap-2">
          <PriceTag isFree={module.isFree} price={module.price} basePrice={module.basePrice} promoPrice={module.promoPrice} gratisLabel={labels.gratis} />
          <div className="self-end">
            <ModuleCTA module={module} labels={labels} />
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function CompactCourseCard({ course, labels }: {
  course: Course;
  labels: { gratis: string; materi: string; mulai: string; diKeranjang: string; cobaGratis: string; daftar: string };
}) {
  const accentColor = course.examTypes[0]?.color ?? '#0f766e';
  const hasDiscount = course.promoPrice != null && course.basePrice != null && course.promoPrice < course.basePrice;
  const discountPct = hasDiscount ? Math.round(((course.basePrice! - course.promoPrice!) / course.basePrice!) * 100) : 0;

  return (
    <div className="relative h-full">
    <Link href={`/courses/${course.slug}`} className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-200 hover:shadow-md transition-all h-full">
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
        {course.coverImageUrl ? (
          <Image src={course.coverImageUrl} alt={course.title} fill sizes="(max-width: 640px) 50vw, 33vw" className="object-cover object-left group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <CoverPlaceholder label={course.examTypes[0]?.name ?? course.title} accentColor={course.examTypes[0]?.color ?? accentColor} compact />
        )}
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 flex-wrap mb-1.5">
          {course.examTypes.slice(0, 1).map(et => (
            <span key={et.slug} className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}>
              {et.name}
            </span>
          ))}
        </div>
        <h4 className="font-bold text-slate-900 mb-1 line-clamp-2 text-sm sm:text-base group-hover:text-teal-700 transition-colors">{course.title}</h4>
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2 mt-auto">
          <span className="flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{course.totalLessons} {labels.materi}</span>
          {course.totalDurationMins > 0 && (
            <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{formatDuration(course.totalDurationMins)}</span>
          )}
        </div>
        <div className="pt-2 sm:pt-3 border-t border-slate-100 flex flex-col gap-2">
          <PriceTag isFree={course.isFree} price={course.basePrice ?? 0} basePrice={course.basePrice ?? 0} promoPrice={course.promoPrice} gratisLabel={labels.gratis} />
          <div className="self-end">
            <CourseCTA course={course} labels={labels} />
          </div>
        </div>
      </div>
    </Link>
    <CardRibbon isFree={course.isFree} discountPct={discountPct} gratisLabel={labels.gratis} />
    </div>
  );
}

/* ── Bento Grid ─────────────────────────────────────── */

// Featured card is 2x2. With 9 items that fills 3 columns (2 + 6) and 4 columns
// (4 + 4) exactly. Below `lg` only the featured card and the next four show, so
// the two-column grid never ends on a lone card.
const BENTO_GRID =
  'grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-lg:[&>*:nth-child(n+6)]:hidden';

function ModuleBentoGrid({ modules, labels }: {
  modules: TestModule[];
  labels: { gratis: string; ujian: string; waktuFleksibel: string; mulai: string; diKeranjang: string; cobaGratis: string; ikuti: string };
}) {
  if (modules.length === 0) return null;
  const [featured, ...rest] = modules;

  return (
    <div className={BENTO_GRID}>
      <div className="col-span-2 lg:col-span-2 lg:row-span-2">
        <FeaturedModuleCard module={featured} labels={labels} />
      </div>
      {rest.map((m) => (
        <CompactModuleCard key={m.id} module={m} labels={labels} />
      ))}
    </div>
  );
}

function CourseBentoGrid({ courses, labels }: {
  courses: Course[];
  labels: { gratis: string; materi: string; mulai: string; diKeranjang: string; cobaGratis: string; daftar: string };
}) {
  if (courses.length === 0) return null;
  const [featured, ...rest] = courses;

  return (
    <div className={BENTO_GRID}>
      <div className="col-span-2 lg:col-span-2 lg:row-span-2">
        <FeaturedCourseCard course={featured} labels={labels} />
      </div>
      {rest.map((c) => (
        <CompactCourseCard key={c.id} course={c} labels={labels} />
      ))}
    </div>
  );
}

/* ── Personalization loading skeleton ───────────────── */

// Shown only while the segment policy for the first personalized render on
// this page is still resolving (cache miss) — avoids a flash of the full,
// unfiltered catalog that then reflows once the policy loads. Mirrors the
// bento grid's shape (one featured slot + compact cards) so the layout
// doesn't jump when real content swaps in.
function CatalogGridSkeleton({ count }: { count: number }) {
  return (
    <div className={BENTO_GRID} aria-hidden="true">
      <div className="col-span-2 lg:col-span-2 lg:row-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden animate-pulse">
        <div className="aspect-[16/9] lg:aspect-auto lg:h-full bg-slate-100" />
      </div>
      {Array.from({ length: Math.max(count - 1, 0) }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden animate-pulse">
          <div className="aspect-[16/9] bg-slate-100" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-slate-100 rounded w-2/3" />
            <div className="h-3 bg-slate-100 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main Catalog Section ───────────────────────────── */

type Tab = 'ujian' | 'belajar';

interface CatalogProps {
  modules: TestModule[];
  courses: Course[];
  maxItems?: number;
}

export function Catalog({ modules, courses, maxItems = 6 }: CatalogProps) {
  const t = useTranslations('home.catalog');
  const [activeTab, setActiveTab] = useState<Tab>('ujian');
  const { ownedModuleIds, ownedCourseIds } = useOwnedItems();
  const { segment, interests } = usePreferences();

  const [showAll, setShowAll] = useState(false);

  // Reset back to the personalized view whenever the segment itself changes
  // (matches the previous behavior of the inline fetch effect).
  useEffect(() => {
    setShowAll(false);
  }, [segment]);

  // Segment de-noising data — shared/cached across every consumer of the
  // segment-policy endpoint on the page (strip, catalog, /tests bundles) via
  // useSegmentPolicy. `visibleSlugs === null` covers "no segment, policy
  // fetch failed" — never hide on a bad state, same as before.
  // `policyPending` covers "segment set, first fetch on this page still in
  // flight" — shown as a skeleton instead of a flash of the unfiltered
  // catalog. See docs/personalization-spec.md — UX surface D.
  const { examTypes: segmentExamTypes, status: policyStatus } = useSegmentPolicy(segment);
  const policyPending = !!segment && policyStatus === 'loading';
  const visibleSlugs = useMemo(() => {
    if (!segment || policyStatus !== 'ready') return null;
    return new Set(segmentExamTypes.map((et) => et.slug));
  }, [segment, policyStatus, segmentExamTypes]);

  const labels = {
    gratis: t('gratis'),
    ujian: t('ujian'),
    materi: t('materi'),
    waktuFleksibel: t('waktuFleksibel'),
    diKeranjang: t('diKeranjang'),
    cobaGratis: t('cobaGratis'),
    ikuti: t('ikuti'),
    daftar: t('daftar'),
    mulai: t('mulai'),
  };

  // Segment de-noise, then sort: interest exam type first, unowned-before-owned
  // as the tiebreaker within each group. With no pref set this reduces to the
  // exact same owned-vs-not sort as before — no regression.
  const sortedModules = useMemo(() => {
    const visible = filterBySegment(modules, visibleSlugs, showAll);
    return sortByInterest(visible, interests, (m) => ownedModuleIds.has(m.id));
  }, [modules, visibleSlugs, showAll, interests, ownedModuleIds]);

  const sortedCourses = useMemo(() => {
    const visible = filterBySegment(courses, visibleSlugs, showAll);
    return sortByInterest(visible, interests, (c) => ownedCourseIds.has(c.id));
  }, [courses, visibleSlugs, showAll, interests, ownedCourseIds]);

  const displayModules = sortedModules.slice(0, maxItems);
  const displayCourses = sortedCourses.slice(0, maxItems);

  const hasModules = displayModules.length > 0;
  const hasCourses = displayCourses.length > 0;

  // Personalization hid everything on the active tab even though the
  // unfiltered catalog has items — offer the escape hatch instead of a
  // silent empty section.
  const modulesPersonalizedEmpty = !hasModules && modules.length > 0 && !!segment && !showAll;
  const coursesPersonalizedEmpty = !hasCourses && courses.length > 0 && !!segment && !showAll;

  if (!hasModules && !hasCourses && !modulesPersonalizedEmpty && !coursesPersonalizedEmpty) return null;

  const tabs: { key: Tab; label: string; count: number; href: string }[] = [
    { key: 'ujian', label: t('paketUjian'), count: modules.length, href: '/tests' },
    { key: 'belajar', label: t('paketBelajar'), count: courses.length, href: '/courses' },
  ];

  const availableTabs = tabs.filter(tab =>
    tab.key === 'ujian' ? (hasModules || modulesPersonalizedEmpty) : (hasCourses || coursesPersonalizedEmpty)
  );
  const currentTab = availableTabs.find(tab => tab.key === activeTab) ?? availableTabs[0];
  const currentTabPersonalizedEmpty = currentTab.key === 'ujian' ? modulesPersonalizedEmpty : coursesPersonalizedEmpty;

  return (
    <section className="py-10 bg-slate-50">
      <div className={PAGE_CONTAINER}>
        {/* Header with chips */}
        <div className="mb-6">
          {/* Heading + description on the left, the personalization launcher
              (segment/interest filter for this catalog) beside it — right-aligned
              on desktop, stacked below on mobile. See docs/personalization-spec.md. */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{t('title')}</h2>
              <p className="text-sm text-slate-500 mt-1">{t('description')}</p>
            </div>
            <div className="shrink-0 sm:pt-1">
              <PersonalizationLauncher />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
          {/* A one-tab tab bar is just a decorative pill — the section heading
              already says what this is. Returns on its own once Kursus Mandiri
              ships and there are two tabs to switch between. */}
          <div className="flex items-center gap-2">
            {availableTabs.length > 1 && availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  currentTab.key === tab.key
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-300 hover:text-teal-600'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1.5 text-xs ${currentTab.key === tab.key ? 'text-teal-100' : 'text-slate-400'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <Link
            href={currentTab.href}
            className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors hidden sm:block"
          >
            {t('lihatSemua')}
          </Link>
          </div>

          {/* Escape hatch — segment de-noising never hard-locks the catalog */}
          {segment && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-3">
              <SlidersHorizontal className="w-3.5 h-3.5 text-teal-500 shrink-0" />
              <span>{showAll ? t('showingAll') : t('showingPersonalized')}</span>
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                aria-pressed={showAll}
                className="font-semibold text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
              >
                {showAll ? t('personalizeAgain') : t('showAllToggle')}
              </button>
            </div>
          )}
        </div>

        {/* Bento Grid */}
        {policyPending ? (
          <CatalogGridSkeleton count={maxItems} />
        ) : currentTabPersonalizedEmpty ? (
          <CatalogEmptyState
            hasFilters
            onReset={() => setShowAll(true)}
            itemLabel={currentTab.key === 'ujian' ? t('paketUjian') : t('paketBelajar')}
            allHref={currentTab.href}
          />
        ) : currentTab.key === 'ujian' ? (
          <ModuleBentoGrid modules={displayModules} labels={labels} />
        ) : (
          <CourseBentoGrid courses={displayCourses} labels={labels} />
        )}

        {/* Mobile "Lihat Semua" */}
        <div className="mt-4 text-center sm:hidden">
          <Link
            href={currentTab.href}
            className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
          >
            {t('lihatSemuaTab', { label: currentTab.label })}
          </Link>
        </div>
      </div>
    </section>
  );
}
